import { defineStore } from 'pinia'
import { ref } from 'vue'

// Lightweight app-wide toast/snackbar store. The app had no shared toast
// surface — components each rolled their own local `showToast`. This is the
// single reusable one: push a message, it auto-dismisses, and a single
// <ToastHost> mounted at the app root renders the stack. Kept intentionally
// minimal (type + message + timeout); richer needs (actions, persistence,
// a notification center) belong in a dedicated feature, not here.

export type ToastType = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  type: ToastType
  message: string
  /** ms until auto-dismiss; 0 = sticky (dismiss by hand). */
  timeout: number
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])
  let seq = 0

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  function push(type: ToastType, message: string, timeout = 5000): number {
    const id = ++seq
    toasts.value.push({ id, type, message, timeout })
    if (timeout > 0) {
      setTimeout(() => dismiss(id), timeout)
    }
    return id
  }

  const success = (message: string, timeout = 5000) => push('success', message, timeout)
  const error = (message: string, timeout = 8000) => push('error', message, timeout)
  const info = (message: string, timeout = 5000) => push('info', message, timeout)

  return { toasts, push, success, error, info, dismiss }
})
